import { Component, ChangeDetectionStrategy } from '@angular/core';
import { MessageObservable } from '../../../../services.module/message.observable';
import { AuthService } from '../../../../services.module/auth.service';
import { MessageModel } from '../../../../model.module/modals.model';
import { ValidatorInput } from '../../../../model.module/validation/validation-descriptor.entity';
import { UserService } from '../../../../providers.module/user.service';
import { RelationApiService } from '../../../../../generated/api/relation/relation.service';
import {
    BeziehungenZuKundeResponse,
    MaklerRelationData,
    Notification,
    NotificationType,
    SteuerberaterRelationData
} from '../../../../../generated/api/kreditportalBackendAPI.schemas';
import { KundenRelationsModel } from '../../../../model.module/kunde-relations.model';


/**
 * Drecks-Code.
 */
@Component({
    selector: 'kunden-maklermandat',
    templateUrl: './kd-maklermandat.component.html',
    styleUrls: ['../../../commonless/content.less', '../../../commonless/meine-daten.less'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class KundenMaklerMandatComponent {

    model: KundenRelationsModel = new KundenRelationsModel();

    messageLineMakler: Notification = {
        message: 'Sie können Ihrem für das Kreditportal registrierten Vermittler Zugang zu sämtlichen Ihrer Daten einräumen. Der Vermittler kann dann alle Funktionalitäten des Kreditportals für Sie in Ihrem Namen nutzen. Um Ihrem Vermittler Zugang zu Ihren Daten zu gewähren, ist die Eingabe einer Vermittler-Portal-Nummer notwendig, die Sie von Ihrem Vermittler erhalten.',
        type: NotificationType.info
    };

    notificationStbr: Notification = {
        message: 'Sie können Ihrem für das Kreditportal registrierten Steuerberater Zugang zu sämtlichen Ihrer Daten einräumen. Der Steuerberater kann dann in Ihrem Namen Ihren Jahresabschluss hochladen und eine Obligoliste anfordern. Um Ihrem Steuerberater Zugang zu Ihren Daten zu gewähren, ist die Eingabe einer Steuerberater-Portal-Nummer notwendig, die Sie von Ihrem Steuerberater erhalten.',
        type: NotificationType.info
    };

    vermittlerStep = 1;
    steuerberaterStep = 1;

    vermittlerChecked = false;
    steuerberaterChecked = false;

    alreadyHasMakler = false;
    alreadyHasStbr = false;

    initialized = false;

    constructor(
        private readonly messageObservable: MessageObservable,
        private readonly authService: AuthService,
        private readonly userService: UserService,
        private readonly relationApiService: RelationApiService
    ) {
        const rootUserId = this.authService.getRootUserId();

        this.relationApiService.getBeziehungenZuKunde(rootUserId).subscribe(
            res => this.examineRes(res)
        );
    }

    validateField(field: ValidatorInput): void {
        this.model.validateField(field);
    }

    setVermittlerNumber(step: number) {
        if (step === 1 && !this.alreadyHasMakler) {
            this.vermittlerChecked = false;
        } else {
            this.model.validateField(this.model.maklerId);

            if (!this.model.maklerId.valid) {
                return;
            }
        }

        this.vermittlerStep = step;
    }

    setSteuerberaterNumber(step: number) {
        if (step === 1 && !this.alreadyHasStbr) {
            this.steuerberaterChecked = false;
        } else {
            this.model.validateField(this.model.steuerberater);

            if (!this.model.steuerberater.valid) {
                return;
            }
        }

        this.steuerberaterStep = step;
    }

    examineRes(res: BeziehungenZuKundeResponse) {
        this.model.copyData(res);
        this.alreadyHasMakler = res.maklerId !== null;
        this.alreadyHasStbr = res.steuerberaterId !== null;

        if (this.alreadyHasMakler) {
            this.vermittlerStep = 2;
        }

        if (this.alreadyHasStbr) {
            this.steuerberaterStep = 2;
        }

        this.vermittlerChecked = this.alreadyHasMakler;
        this.steuerberaterChecked = this.alreadyHasStbr;
        this.initialized = true;
    }

    hireOrFireVermittler(forceFalse = false) {
        const rootUserId = this.authService.getRootUserId();
        const maklerId = (this.model.maklerId.value + '').replace('-', '');

        const maklerRelationData: MaklerRelationData = {
            vsnr: rootUserId,
            maklerId
        };

        if (this.vermittlerChecked && !forceFalse) {
            this.model.validateField(this.model.maklerId);

            if (!this.model.maklerId.valid) {
                return;
            }

            this.relationApiService.anlegenMakler(maklerRelationData).subscribe(() => {
                this.vermittlerStep = 1;
                this.messageObservable.notify(new MessageModel(
                    'Vielen Dank.',
                    ['Die Freigabe wurde erteilt.', 'Ihr Vermittler kann Ihre Daten nun einsehen.'],
                ));

                this.updateView();
            });
        } else {
            this.relationApiService.loeschenMakler(maklerRelationData).subscribe(() => {
                this.vermittlerStep = 1;
                this.messageObservable.notify(new MessageModel(
                    'Vielen Dank.',
                    ['Die Freigabe wurde entzogen.', 'Ihr Vermittler kann Ihre Daten ab sofort nicht mehr einsehen.'],
                ));

                this.updateView();
            });
        }
    }

    hireOrFireSteuerberater(forceFalse = false) {
        const rootUserId = this.authService.getRootUserId();
        const steuerberaterId = this.model.steuerberater.value;

        const steuerberaterRelationData: SteuerberaterRelationData = {
            vsnr: rootUserId,
            steuerberaterId
        }

        if (this.steuerberaterChecked && !forceFalse) {
            this.model.validateField(this.model.steuerberater);

            if (!this.model.steuerberater.valid) {
                return;
            }

            this.relationApiService.anlegenSteuerberater(steuerberaterRelationData).subscribe(() => {
                this.steuerberaterStep = 1;
                this.messageObservable.notify(new MessageModel(
                    'Vielen Dank.',
                    ['Die Freigabe wurde erteilt.', 'Ihr Steuerberater kann für Sie nun einen Jahresabschluss hochladen und Obligolisten anfordern.'],
                ));

                this.updateView();
            });
        } else {
            this.relationApiService.loeschenSteuerberater(steuerberaterRelationData).subscribe(() => {
                this.steuerberaterStep = 1;
                this.messageObservable.notify(new MessageModel(
                    'Vielen Dank.',
                    ['Die Freigabe wurde entzogen.', 'Ihr Steuerberater kann ab sofort keinen Jahresabschluss mehr hochladen oder Obligolisten anfordern.'],
                ));

                this.updateView();
            });
        }
    }

    private updateView() {
        const rootUserId = this.authService.getRootUserId();

        this.relationApiService.getBeziehungenZuKunde(rootUserId).subscribe(
            res => this.examineRes(res)
        );
    }

}
